import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { postFeedbackForm } from '../api/postFeedbackForm';
import { ConsentCheckBox } from '../components/consentCheckbox/ConsentCheckBox';
import { Footer } from '../components/Footer/Footer';
import { FeedbackFieldForm } from '../components/form/FeedbackFieldForm';
import { FormStyled } from '../components/form/Form.styled';
import { InputTexFieldForm } from '../components/form/InputTextField';
import { Messageform } from '../components/form/MessageForm';
import { Header } from '../components/Header/Header';
import { routes } from '../constants/constants';
import { extractUrlParam } from '../helper/extractUrlParam';
import { useLocation } from '../react-router-hooks';
import { theme } from '../theme';

export const FeedbackFormScreen = () => {
  const { t } = useTranslation();
  const { location } = useLocation();
  const [rateExperienceState, setRateExperienceState] = useState(0);
  const [rateFunctionalityState, setRateFunctionalityState] = useState(0);
  const [rateInstructionsState, setRateInstructionsState] = useState(0);
  const [rateUserFriendlinessState, setRateUserFriendlinessState] = useState(0);
  const [paramsState, setParamsState] = useState<any>();
  const [disableButton, setDisableButton] = useState(false);
  const [checked, setChecked] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState('');

  useEffect(() => {
    if (!location.state || !location.state.uuid) {
      const url = location.search;
      const rateNumber = Number(extractUrlParam({ url, type: 'rating' }));
      setParamsState({
        uuid: extractUrlParam({ url, type: 'uuid' }),
        model: extractUrlParam({ url, type: 'model' }),
        appVersion: extractUrlParam({ url, type: 'appVersion' }),
        systemVersion: extractUrlParam({ url, type: 'systemVersion' }),
        customer: extractUrlParam({ url, type: 'customer' }),
        rating: rateNumber
      });
      if (rateNumber > 0) {
        setRateExperienceState(rateNumber);
      }
    } else {
      setParamsState(location.state);
    }
  }, [location]);

  return (
    <div className="root">
      <Header isFormFeedback={true} />
      <div id="main" className="main">
        <FormStyled.ContainerMain>
          <FormStyled.Container>
            <Messageform
              title="How was your experience?"
              message="Thank you for using Nect! Your opinion matters to us as we are always looking for space to improve. Help us understand what worked and didn’t work for you."
              link="I need help with the process"
              to={{ pathname: routes.HELPFORM, state: paramsState }}
            />
            <div style={{ marginTop: 24, marginBottom: 48 }}>
              <Formik
                initialValues={{
                  experience: '',
                  functionality: '',
                  instructions: '',
                  userFriendliness: '',
                  rateExperience: 0,
                  rateFunctionality: 0,
                  rateInstructions: 0,
                  rateUserFriendliness: 0,
                  email: '',
                  checkBox: ''
                }}
                validate={values => {
                  const errors: any = {};
                  const email: string = values.email.trim();
                  if (!values.experience) {
                    errors.experience = t('Please write down your reason.');
                  } else {
                    setDisableButton(false);
                  }
                  if (!values.rateExperience) {
                    errors.rateExperience = t('Please select a score.');
                  }
                  if (email) {
                    setDisableButton(false);
                    if (
                      !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/i.test(
                        email
                      )
                    ) {
                      errors.email = t(
                        'Your email address should contain an ‘@’.'
                      );
                    }
                    if (!checked) {
                      errors.checkBox = t(
                        'Please check the box below before you submit'
                      );
                    }
                  }
                  return errors;
                }}
                onSubmit={async (values, { resetForm }) => {
                  if (isSubmitting) return;

                  setSubmitting(true);
                  try {
                    const response = await postFeedbackForm({
                      state: paramsState,
                      values
                    });
                    if (response && response.ok) {
                      resetForm();
                      setRateExperienceState(0);
                      setRateFunctionalityState(0);
                      setRateInstructionsState(0);
                      setRateUserFriendlinessState(0);
                      setDisableButton(true);
                      setFormState('ok');
                    } else {
                      setFormState('error');
                    }
                  } catch (error) {
                    console.error(error);
                    setFormState('error');
                  }
                  setSubmitting(false);
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleSubmit,
                  setFieldValue
                }) => (
                  <form
                    onSubmit={handleSubmit}
                    style={{
                      width: '100%'
                    }}
                  >
                    <FeedbackFieldForm
                      name="experience"
                      value={values.experience}
                      onChange={handleChange}
                      question="How is your overall experience? <b>*required</b>"
                      hint="Tell us why you gave this score"
                      color={
                        errors.experience && touched.experience
                          ? theme.red
                          : theme.gray
                      }
                      nameRating="rateExperience"
                      onRateChange={(number: number) => {
                        setFieldValue('rateExperience', number);
                        setRateExperienceState(number);
                      }}
                      selectNumber={rateExperienceState}
                      errorScoreMessage={
                        errors.rateExperience &&
                        touched.rateExperience &&
                        errors.rateExperience
                          ? errors.rateExperience
                          : ''
                      }
                    />
                    <FormStyled.ValidationMessage>
                      {errors.experience &&
                        touched.experience &&
                        errors.experience}
                    </FormStyled.ValidationMessage>
                    <FeedbackFieldForm
                      name="functionality"
                      value={values.functionality}
                      onChange={handleChange}
                      question="What do you think of the <strong>functionality</strong> of the Nect app? <label>*optional</label>"
                      hint="Tell us why you gave this score"
                      onRateChange={(number: number) => {
                        setFieldValue('rateFunctionality', number);
                        setRateFunctionalityState(number);
                      }}
                      selectNumber={rateFunctionalityState}
                      nameRating="rateFunctionality"
                    />
                    <FeedbackFieldForm
                      name="instructions"
                      value={values.instructions}
                      onChange={handleChange}
                      question="What do you think of the <strong>instructions and tips</strong> in the Nect app? <label>*optional</label>"
                      hint="Tell us why you gave this score"
                      onRateChange={(number: number) => {
                        setFieldValue('rateInstructions', number);
                        setRateInstructionsState(number);
                      }}
                      selectNumber={rateInstructionsState}
                      nameRating="rateInstructions"
                    />
                    <FeedbackFieldForm
                      name="userFriendliness"
                      value={values.userFriendliness}
                      onChange={handleChange}
                      question="What do you think of the <strong>user-friendliness</strong> of the Nect app? <label>*optional</label>"
                      hint="Tell us why you gave this score"
                      onRateChange={(number: number) => {
                        setFieldValue('rateUserFriendliness', number);
                        setRateUserFriendlinessState(number);
                      }}
                      selectNumber={rateUserFriendlinessState}
                      nameRating="rateUserFriendliness"
                    />
                    <InputTexFieldForm
                      name="email"
                      value={values.email}
                      question="If you want us to be able to reply to your feedback, please provide <strong>your email</strong>. <label>*optional</label>"
                      hint="max@mustermann.com"
                      onChange={handleChange}
                      color={
                        errors.email && touched.email ? theme.red : theme.gray
                      }
                    />
                    <FormStyled.ValidationMessage>
                      {errors.email && touched.email && errors.email}
                    </FormStyled.ValidationMessage>
                    <FormStyled.ButtonSubmit
                      type="submit"
                      disabled={disableButton}
                      style={{
                        color: disableButton ? theme.gray : theme.primary,
                        background: disableButton
                          ? theme.grayLight
                          : theme.primaryLight,
                        borderColor: disableButton
                          ? theme.grayLight
                          : theme.primaryLight
                      }}
                    >
                      {disableButton ? t('Sent') : t('Submit')}
                    </FormStyled.ButtonSubmit>
                    {formState === 'ok' && (
                      <FormStyled.SentMessage style={{ color: theme.primary }}>
                        {t('Thank you!')}
                      </FormStyled.SentMessage>
                    )}
                    {formState === 'error' && (
                      <FormStyled.SentMessage
                        style={{ color: theme.red }}
                        dangerouslySetInnerHTML={{
                          __html: t(
                            "An unknown error has occurred. Please contact our support team at <a href='mailto:service@nect.com'>service@nect.com</a>."
                          )
                        }}
                      />
                    )}
                    {values.email && (
                      <div>
                        <FormStyled.ValidationMessage>
                          {errors.checkBox &&
                            touched.checkBox &&
                            errors.checkBox}
                        </FormStyled.ValidationMessage>
                        <FormStyled.BottomSpan>
                          <ConsentCheckBox
                            onChange={() => {
                              setChecked(!checked);
                            }}
                            checked={checked}
                            disabled={false}
                            text={t(
                              "I have read and understood Nect's Data Protection Policy. Your data provided here will only be processed for other purposes if you have given separate consent or if a legal regulation permits this processing."
                            )}
                          />
                        </FormStyled.BottomSpan>
                      </div>
                    )}
                  </form>
                )}
              </Formik>
            </div>
          </FormStyled.Container>
        </FormStyled.ContainerMain>
      </div>
      <Footer />
    </div>
  );
};
